import { constants, http } from 'lib_ui-services';
import pThrottle from 'p-throttle';

const { notificationTypes } = constants;

// We are rate limited to 50 requests per path per minute
const MAX_REQUEST_PER_TIME_FRAME = 50;
const REQUEST_RATE_TIME_FRAME = 60000; // 1 minute in ms
const BATCH_SIZE = 100;
const FORBIDDEN = 403;

export default {
    verb: 'doingCreate',
    namespace: 'import',
    relation: 'import',
    description: 'Doing the actual import, by sending the data over to the server',
    // this is the actual logic:
    logic: doingCreate,
    // error handling to avoid the progress staying on the screen:
    onError
};

const _p = {
    pThrottle
};
export const _private = _p;

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function doingCreate({ data, dispatch, log }) {
    const { newRecord } = data;
    const { _id, title, data: parsedData, fileSize, foreignNamespace, foreignRelation } = newRecord;
    const url = `/api/${foreignNamespace}/${foreignRelation}/${_id}/import`;

    // calculate the average chunk size
    const totalRecords = parsedData.length;
    if (totalRecords === 0) return;

    const averageRecordSize = fileSize / totalRecords;
    log.info(`Importing ${totalRecords} records into ${foreignNamespace}:${foreignRelation}.`);

    const sendBatch = createThrottledBatchSender(url);

    const { showProgress, closeProgress } = createProgressHandler(dispatch, title, totalRecords);
    let failed = [];
    // Server-authoritative counts read back from the import:import record in each batch
    // response. They account for server-side fan-out (e.g. Serialized Each model+quantity,
    // where one source row becomes N item:item records), which the client-side row count
    // (`totalRecords`) cannot. Left null for import paths that don't report them so the
    // notification falls back to the row count. Counts are cumulative across batches ($inc on
    // the server), so the latest non-null response holds the running totals.
    let importedRecords = null;
    let recordsCreated = null;
    // Rows and records counted apart (#2282), for imports where one CSV row is not one record —
    // orders are grouped from many rows before they are sent. Cumulative on the server like the
    // totals above, so the latest response holds the running counts.
    let counts = null;

    await showProgress(0, 'Starting import process');
    toggleSpinner(dispatch, true);

    for (let chunkIndex = 0; chunkIndex * BATCH_SIZE < totalRecords; chunkIndex++) {
        const chunk = parsedData.slice(chunkIndex * BATCH_SIZE, (chunkIndex + 1) * BATCH_SIZE);
        const batch = createBatch(chunk, newRecord, chunkIndex, averageRecordSize, totalRecords);
        try {
            const result = await sendBatch(batch);
            const importRecord = result?.[0];
            failed = failuresAfterBatch(failed, importRecord);
            if (typeof importRecord?.importedRecords === 'number') {
                importedRecords = importRecord.importedRecords;
            }
            if (typeof importRecord?.totalRecords === 'number') {
                recordsCreated = importRecord.totalRecords;
            }
            counts = rowAndRecordCountsOf(importRecord) ?? counts;
        } catch (error) {
            log.error(`Error importing batch ${chunkIndex + 1}:`, error);
            // A refusal applies to the whole import, not to this batch (e.g. order import for a
            // tenant without Order Confirmation, #2282). Carrying on would send every other
            // batch to the same refusal and then report the import as a success.
            if (error?.status === FORBIDDEN) {
                toggleSpinner(dispatch, false);
                closeProgressDialog(dispatch);
                refuseImport(dispatch, title, chunkIndex);
            }
        }

        await showProgress(
            Math.min((chunkIndex + 1) * BATCH_SIZE, totalRecords),
            `Imported ${(chunkIndex + 1) * BATCH_SIZE} out of ${totalRecords} records`
        );
    }

    await closeProgress(`Completed import of ${totalRecords} records`);
    toggleSpinner(dispatch, false);

    return {
        ...data,
        result: {
            title,
            // Count of entries sent — kept for progress semantics and as the fallback when the
            // server does not report record-level counts.
            totalRecords,
            // CSV rows read. Differs from totalRecords when rows were grouped before sending.
            totalRows: newRecord.sourceRowCount ?? totalRecords,
            // Server-reported counts (present for the generic import path); account for fan-out.
            importedRecords,
            recordsCreated,
            counts,
            failed
        }
    };
}

function onError({ error, dispatch }) {
    closeProgressDialog(dispatch);
    throw error;
}

/**
 * Utility functions
 */
function createThrottledBatchSender(url) {
    // throttle at 1 per part of interval, rather than MAX_REQUEST_PER_TIME_FRAME as limit and REQUEST_RATE_TIME_FRAME as interval
    // to avoid an initial surge and avoid any risk of hitting rate limits:
    const throttle = _p.pThrottle({
        limit: 1,
        interval: REQUEST_RATE_TIME_FRAME / MAX_REQUEST_PER_TIME_FRAME
    });
    return throttle(batch => http.post(url, batch));
}

function createProgressHandler(dispatch, title, totalRecords) {
    const showProgress = progressNotifier(dispatch, `Importing ${title}`, totalRecords);
    return {
        showProgress,
        closeProgress: async message => {
            await showProgress(totalRecords, message);
            closeProgressDialog(dispatch);
        }
    };
}

function createBatch(chunk, newRecord, chunkIndex, averageRecordSize, totalRecords) {
    // eslint-disable-next-line no-unused-vars -- sourceRowCount is for the client's summary only
    const { _id, title, fileSize, foreignNamespace, foreignRelation, sourceRowCount, ...importDetails } = newRecord;
    const chunkSize = Math.round(chunk.length * averageRecordSize);
    const progress = Math.round((chunkIndex * BATCH_SIZE + chunk.length) * averageRecordSize);

    // the server uses chunk size rather than progress,
    // as theoretically there is a chance that it might process these batches in parallel/out of order
    // chunkSize will allow it to simply add the current value to whatever was stored (rather than replace it)
    return {
        _id,
        title,
        namespace: foreignNamespace,
        relation: foreignRelation,
        ...importDetails,
        data: chunk,
        fileSize,
        totalRecords,
        progress,
        chunkSize
    };
}

function toggleSpinner(dispatch, isSpinning) {
    const message = isSpinning ? 'Removing' : 'Removal Complete';
    dispatch(
        { busy: isSpinning, source: 'httpSync', message, type: notificationTypes.SYNC_BUSY },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}

function progressNotifier(dispatch, title, total) {
    return (current, description) =>
        dispatch(
            { mainTitle: 'Importing', description, title, current, total },
            { verb: 'update', namespace: 'application', relation: 'progress' }
        );
}

function closeProgressDialog(dispatch) {
    return dispatch({ mainTitle: 'Importing' }, { verb: 'reset', namespace: 'application', relation: 'progress' });
}

/**
 * Tell the operator the server refused the import, and stop it.
 *
 * Thrown so the summary that follows a completed import never runs: a refused import must not be
 * reported as one that succeeded.
 *
 * @param {Function} dispatch
 * @param {string} title import name
 * @param {number} chunkIndex the batch that was refused
 */
function refuseImport(dispatch, title, chunkIndex) {
    const imported = chunkIndex === 0 ? 'Nothing was imported.' : 'Batches sent before it were imported.';
    // The HTTP client does not pass a 403's body on, so the server's own reason is not available
    // here; name the likely causes instead.
    const message =
        `Import "${title}" failed: the server refused this import (403 Forbidden). The feature it needs ` +
        `may not be enabled for this tenant, or your role may not be allowed to import here. ${imported}`;
    dispatch(
        {
            message,
            timeout: 5000,
            addToList: true,
            variant: 'error',
            type: notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
    throw new Error(message);
}

/**
 * Every failure reported so far, once this batch's response is in.
 *
 * Two kinds of response come back. The rules-engine returns the import record itself, whose
 * `failed` holds every failure of the import so far (the first 1000) — like its counts, a running
 * total, so it REPLACES the list. Appending it after every batch repeated the earlier batches'
 * failures in the failed-records CSV (#7596, reintroduced in May 2026), and with orders that
 * meant a corrected re-import summed the repeated rows into doubled quantities (#2282). Device
 * management returns only this batch's failures, without the totals, so those are added.
 *
 * @param {Object[]} failedSoFar
 * @param {Object} [importRecord]
 */
function failuresAfterBatch(failedSoFar, importRecord) {
    if (!Array.isArray(importRecord?.failed)) return failedSoFar;
    const processed = processFailedRecords(importRecord.failed);
    if (typeof importRecord.totalRecords === 'number') return processed;
    return [...failedSoFar, ...processed];
}

/**
 * The row and record counts an import record reports, or null when it reports none.
 * @param {Object} [importRecord]
 */
function rowAndRecordCountsOf(importRecord) {
    const fields = ['rowsImported', 'rowsFailed', 'recordsImported', 'recordsFailed'];
    if (!fields.every(field => typeof importRecord?.[field] === 'number')) return null;
    return Object.fromEntries(fields.map(field => [field, importRecord[field]]));
}

/**
 * Process failed records to extract error details
 * @param {Array} failedRecords - Array of records that failed import
 * @returns {Array} Processed failed records with error details
 */
export function processFailedRecords(failedRecords) {
    return failedRecords.map(record => {
        const { errors, ...recordData } = record;

        // records that fail client-side validation will have errors as an array of objects where we can
        // get access to the column that failed and more detail about the error
        // any records that fail server-side only validation will have errors as an array of strings
        // so we just need to return that array of strings, where they'll be formatted for csv output in the didCreate_import_import rule
        //
        // The parser drops a value it refuses, so each failed column's value is put back for the
        // CSV. Every one of them: restoring only the first left the other failed cells blank, and
        // the operator could not see what they had typed there.
        const columnErrors = Array.isArray(errors) ? errors.filter(e => typeof e === 'object' && e?.column) : [];

        if (!columnErrors.length) {
            return record;
        }

        return {
            ...recordData,
            errors,
            ...Object.fromEntries(columnErrors.map(error => [error.column, error.value]))
        };
    });
}
